import type {
  ContractBlockKind,
  ContractContentView,
  ContractDocument,
  ContractEditorPolicy,
  ContractSaveState,
} from "@closer/shared";
import {
  CONTRACT_BLOCK_KIND_NAMES,
  CONTRACT_BLOCK_KINDS,
  CONTRACT_SAVE_STATE_NAMES,
  contractContentLockExplanation,
} from "@closer/shared";
import { Button, Card, Input, Select, Skeleton, Textarea, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";

import { ContractDocumentEditor } from "../../components/ContractDocumentEditor";
import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * Sözleşme METNİ editörü (CLOS-118 `[086]`).
 *
 * ### ⚠ Yapısal editör, zengin metin DEĞİL (ADR-0026)
 *
 * Kanonik içerik madde/blok yapısı; ekran o yapıyı doğrudan düzenliyor. Bir zengin metin
 * kütüphanesi kendi belge modelini, kendi serileştirmesini ve CSP uyumluluk sorumluluğunu
 * getirirdi — üç borç, karşılığında bugün kullanmayacağımız bir yetenek (refleks 9).
 *
 * ### ⚠ Taslak YEREL, sunucu durumu sorguda
 *
 * Kullanıcının yazdığı hâl bileşen state'inde; bu "sunucu verisini `useState`'e kopyalama"
 * yasağının istisnası değil, çünkü düzenlenen taslak sunucu verisi değil, henüz
 * gönderilmemiş kullanıcı girdisi. Kaydetme AÇIK bir komut — otomatik kaydetme CLOS-119
 * `[087]`'nin kendi kartı.
 *
 * ### ⚠ Çakışmada kullanıcının metni SİLİNMİYOR
 *
 * `CONCURRENCY_CONFLICT` geldiğinde ekran yazdıklarını atıp sunucudakini basmıyor: kullanıcı
 * kendi cümlelerini kaybetmeden farkı görebilmeli (`apps/web/CLAUDE.md`: *"kaydı yeniden
 * oku ve kullanıcıya farkı göster; körlemesine tekrar göndermek başkasının değişikliğini
 * ezer"*).
 */

type Taslak = ContractDocument;

function bosMadde() {
  return { id: crypto.randomUUID(), title: "", blocks: [{ kind: "paragraph" as const, text: "" }] };
}

/**
 * Otomatik kaydetme NE ZAMAN ve NE KADAR sonra çalışır — ya da hiç çalışmaz (`null`).
 *
 * ⚠ Karar bir efektin içinde gömülü kalsaydı yalnız zamanlamaya dayalı testlerle
 * ölçülebilirdi; bu kartın kuralları (çakışmada dur, yetki kaybında dur, sınırı aşınca dur)
 * zamanlamadan bağımsız ve tek tek sabitlenmeli. Bu yüzden karar saf bir fonksiyon.
 */
export function otomatikKaydetmeGecikmesi(input: {
  durum: ContractSaveState;
  kirli: boolean;
  hataSayisi: number;
  politika: ContractEditorPolicy;
}): number | null {
  /** Çakışma ve kilit KULLANICININ kararını bekliyor; devam etmek kararı gaspetmek olurdu. */
  if (input.durum === "conflict" || input.durum === "blocked") return null;
  /** Zaten kaydediliyorsa ikinci bir istek, aynı sürümle çakışmaktan başka bir şey yapmaz. */
  if (input.durum === "saving") return null;
  /** Kaydedilmemiş değişiklik yoksa yazacak bir şey de yok (boş trafik, boş audit). */
  if (!input.kirli) return null;

  if (input.durum === "pending") {
    /** Sonsuza kadar denemek yok: sınır politikadan geliyor. */
    if (input.hataSayisi >= input.politika.maxRetryAttempts) return null;
    return input.politika.retryDelayMs;
  }
  return input.politika.autosaveDelayMs;
}

/** Kaydetmeden önce boş bloklar atılıyor — ama SESSİZCE değil: kullanıcı sonucu görüyor. */
function kaydedilebilir(taslak: Taslak): Taslak {
  return {
    clauses: taslak.clauses.map((c) => ({
      ...c,
      blocks: c.blocks.filter((b) => b.text.length > 0),
    })),
  };
}

export function ContractContentPage() {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const { id = "" } = useParams();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [taslak, setTaslak] = useState<Taslak | null>(null);

  /**
   * YAZIM YÜZEYİ: kesintisiz belge mi, madde kartları mı (CLOS-633).
   *
   * ⚠ Varsayılan BELGE. Kart açık: *"her madde/paragraf için ayrı Madde Ekle, kart veya
   * blok oluşturma zorunluluğu yoktur"* ve *"madde/bölüm yapısı varsa YARDIMCI özelliktir;
   * ana yazım modeli değildir."* Kart yüzeyi kaldırılmıyor — sıralama ve blok türü orada
   * yapılıyor — ama artık kullanıcının ilk karşılaştığı şey değil.
   *
   * ⚠ SAKLAMA BİÇİMİ DEĞİŞMİYOR (ADR-0026): kanonik içerik hâlâ madde/blok yapısı. Değişen
   * yalnız yazım yüzeyi; metin kaydedilirken yapıya çevriliyor. HTML saklama, ofset tabanlı
   * işaretler ve yeni editör bağımlılığı hâlâ yok.
   */
  const [yuzey, setYuzey] = useState<"belge" | "maddeler">("belge");
  /**
   * ⚠ BELGE YÜZEYİNİN HAM METNİ (kullanıcı bildirimi, 25 Ağustos).
   *
   * Alan, yazılanı madde yapısına çevirip TEKRAR metne dönüştürerek gösteriyordu. O
   * dönüşüm kayıplı — satır sonu boşluğu kırpılıyor, tek satır sonu düşüyor — ve kayıp
   * karakter ekrana hiç yazılamıyordu: boşluk ve Enter tuşları çalışmıyor, imleç her
   * tuşta sona atlıyordu. Ham metin artık burada duruyor ve alana OLDUĞU GİBİ dönüyor;
   * madde yapısı yalnız kaydetme/versiyon yolunda türetiliyor.
   *
   * `null` = kullanıcı bu yüzeyde henüz yazmadı; metin modelden türetilir. Model dışarıdan
   * değiştiğinde (sunucudan yükleme, dosyadan aktarma, madde yüzeyinde düzenleme) `null`'a
   * dönmek ZORUNLU — yoksa alan bayat metni gösterirdi.
   */
  /**
   * ⚠ Durum KULLANICIYA gösterilen tek gerçek (CLOS-119 `[087]`). Kart: *"sunucuya
   * BAŞARIYLA YAZILMADAN kaydedildi olarak gösterilmez"* — bu yüzden `pending` ile `saved`
   * ayrı durumlar ve aralarındaki geçişi yalnız sunucunun 200'ü yapıyor.
   */
  const [durum, setDurum] = useState<ContractSaveState>("saved");
  const [hataSayisi, setHataSayisi] = useState(0);
  /** Çakışmada KARŞILAŞTIRMA için sunucudaki hâl; yerel taslak silinmiyor. */
  const [sunucuHali, setSunucuHali] = useState<ContractContentView | null>(null);
  const zamanlayici = useRef<ReturnType<typeof setTimeout> | null>(null);

  const icerik = useQuery({
    queryKey: tenantKey(tenantId, "contract", id, "icerik"),
    queryFn: async () =>
      (await api<ContractContentView>(`/api/contracts/${id}/content`, { tenantId })).data,
  });

  const politika = useQuery({
    /**
     * ⚠ ANAHTAR TENANT'LI (CLOS-833). Eskiden düz `["contract-editor-policy"]` idi ve uç
     * `requireTenant`'lı: politika çalışma alanına göre değişebiliyor ama önbellek onu
     * hesap düzeyinde tutuyordu. Alan değiştiren kullanıcı, ÖNCEKİ alanın politikasıyla
     * çizilmiş bir editör görürdü — ve `clearTenantQueries` bu anahtarı tanımadığı için
     * geçişte de temizlenmezdi (ADR-0022 §2).
     */
    queryKey: tenantKey(tenantId, "contract-editor-policy"),
    queryFn: async () =>
      (await api<ContractEditorPolicy>("/api/contracts/editor-policy", { tenantId })).data,
  });

  /**
   * METİNDEN VERSİYON ÜRETME (CLOS-134 `[089]`).
   *
   * ⚠ Bu düğme yoktu: editörle yazılan bir sözleşmede versiyon üretmenin ekranda hiçbir
   * yolu bulunmuyordu. Versiyon geçmişindeki "Yeni çalışma versiyonu" ancak MEVCUT bir
   * versiyondan kopyalıyor; hiç versiyonu olmayan sözleşmede o düğme de çıkmıyor. Sonuç
   * zincirlemeydi: versiyon olmayınca karşılaştırma ve revizyon önerisi de kullanılamıyordu.
   *
   * ⚠ Yeri EDİTÖR, versiyon geçmişi değil: dondurulacak hâl burada yazılıyor ve
   * "kaydedilmemiş değişiklik var mı?" sorusunun cevabı yalnız burada biliniyor.
   *
   * ⚠ `contentRevision` SUNUCUNUN bildirdiği sürüm. Kendi sayacımızı gönderseydik, arada
   * değişmiş bir metni kullanıcının okuduğu metin diye tarihe geçirebilirdik.
   */
  const versiyonUret = useMutation({
    mutationFn: async (contentRevision: number) =>
      (
        await api<{ id: string; versionNumber: number }>(`/api/contracts/${id}/versions`, {
          method: "POST",
          tenantId,
          body: JSON.stringify({ source: "editor", contentRevision }),
        })
      ).data,
    onSuccess: async (yeni) => {
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "contract", id) });
      toast.success(`Versiyon v${yeni.versionNumber} oluşturuldu`);
    },
    onError: (error) => {
      toast.error(error instanceof ApiRequestError ? error.message : "Versiyon oluşturulamadı");
    },
  });

  /**
   * YÜKLENEN BELGEDEN AKTARMA (CLOS-634).
   *
   * ⚠ Gövde göndermiyoruz: kaynağı sunucu buluyor. Belgeyi istemciden alsaydık, çıkarım
   * sonucu yerine herhangi bir metin "yüklenen belgeden geldi" diye kaydedilebilirdi.
   */
  const aktar = useMutation({
    mutationFn: async () =>
      (
        await api<ContractContentView>(`/api/contracts/${id}/content/from-upload`, {
          method: "POST",
          tenantId,
        })
      ).data,
    onSuccess: async () => {
      setTaslak(null);
      setDurum("saved");
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "contract", id) });
      toast.success("Yüklenen belgedeki metin düzenlemeye aktarıldı");
    },
    onError: (hata) => {
      toast.error(hata instanceof ApiRequestError ? hata.message : "Metin aktarılamadı");
    },
  });

  const kaydet = useMutation({
    mutationFn: async (girdi: { document: Taslak; revision: number }) =>
      (
        await api<ContractContentView>(`/api/contracts/${id}/content`, {
          method: "PUT",
          tenantId,
          body: JSON.stringify(girdi),
        })
      ).data,
    onSuccess: async (yeni) => {
      /**
       * ⚠ Kart: *"başarılı kayıt sonrası istemci YALNIZ SUNUCUNUN DÖNDÜRDÜĞÜ GÜNCEL
       * BELİRTEÇLE devam eder."* Yerel sürüm sayacı tutup bir artırsaydık, sunucunun kabul
       * ettiği hâl ile istemcinin sandığı hâl sessizce ayrışırdı.
       *
       * ⚠ Ama TASLAK sunucunun yanıtıyla EZİLMİYOR: kullanıcı kaydetme uçarken yazmaya devam
       * etmiş olabilir ve yanıt onun cümlelerini geri alırdı. Kirlilik yeniden hesaplanıyor;
       * arada yazılan varsa taslak kirli kalıyor ve bir sonraki otomatik kaydetme onu da
       * götürüyor.
       */
      setDurum("saved");
      setHataSayisi(0);
      setSunucuHali(null);
      queryClient.setQueryData(tenantKey(tenantId, "contract", id, "icerik"), yeni);
      /**
       * ⚠ Kardeş bölümler BAYAT işaretleniyor ama HEMEN ÇEKİLMİYOR (`refetchType: "none"`).
       * Hemen çekseydik açık olan içerik sorgusu da yeniden okunur ve yanıt henüz
       * kaydedilmemiş bir hâli taşıdığında (ya da okuma yolu kaydetmeden bir an önce
       * yakalandığında) istemci kendi taslağını "değişmiş" sayıp ikinci bir kaydetme
       * başlatırdı — bir kaydetme döngüsü. Detay ekranı zaten mount olduğunda taze okuyacak.
       */
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "contract", id),
        refetchType: "none",
      });
    },
    onError: async (hata) => {
      const kod = hata instanceof ApiRequestError ? hata.code : null;

      /**
       * ⚠ ÇAKIŞMA sessizce birleştirilmiyor (kartın kuralı). Otomatik kaydetme DURUYOR:
       * devam etseydi her denemede aynı bayat sürümle çarpar ve kullanıcıya kararı hiç
       * sormadan sonsuza kadar denerdi.
       */
      if (kod === "CONCURRENCY_CONFLICT") {
        setDurum("conflict");
        const guncel = await queryClient.fetchQuery({
          queryKey: tenantKey(tenantId, "contract", id, "icerik"),
          queryFn: async () =>
            (await api<ContractContentView>(`/api/contracts/${id}/content`, { tenantId })).data,
        });
        setSunucuHali(guncel);
        return;
      }

      /**
       * ⚠ Yetki, kayıt erişimi ya da durum kaybında yazma DURUYOR — kart: *"istemcideki eski
       * oturum yazma yetkisi sağlamaz."* Yeniden denemek burada zararlı: kapı kapandığı için
       * her deneme yalnız aynı reddi tekrarlar.
       */
      if (kod === "FORBIDDEN" || kod === "NOT_FOUND" || kod === "CONFLICT") {
        setDurum("blocked");
        toast.error(hata instanceof ApiRequestError ? hata.message : "Kaydetme reddedildi");
        return;
      }

      /**
       * ⚠ DOĞRULAMA HATASI ağ arızası DEĞİL (kusur avı, 26 Ağustos).
       *
       * 422 buraya düşünce durum "bekliyor" oluyor ve otomatik kaydetme aynı geçersiz
       * gövdeyi tekrar tekrar gönderiyordu: kullanıcı sonsuza dek "kaydedilmemiş
       * değişiklikler var" görüyor, sunucunun Türkçe açıklamasını (ör. madde başlığı 300
       * karakteri aşamaz) hiç okumuyordu. Sunucu `retryable: false` diyorsa yeniden
       * denemek zararlı: sebep bizde, ağda değil.
       */
      const tekrarlanabilir = hata instanceof ApiRequestError ? hata.retry?.retryable : undefined;
      if (kod === "VALIDATION_ERROR" || tekrarlanabilir === false) {
        setDurum("blocked");
        toast.error(
          hata instanceof ApiRequestError ? hata.message : "Girilen bilgiler kaydedilemedi",
        );
        return;
      }

      /**
       * ⚠ Kalanlar AĞ/sunucu arızası: değişiklikler BEKLİYOR olarak işaretleniyor ve
       * kaydedildi gösterilmiyor. Kart: *"çevrimdışı veya geçici bağlantı kesintisinde yerel
       * değişiklikler açıkça bekleyen durumda tutulur."*
       */
      setDurum("pending");
      setHataSayisi((n) => n + 1);
    },
  });

  const p = politika.data;
  /**
   * ⚠ Kirlilik KAYDEDİLECEK hâl üzerinden ölçülüyor, ham taslak üzerinden değil.
   *
   * Ham taslakla karşılaştırsaydık boş bir blok eklemek sonsuz bir kaydetme döngüsü
   * kurardı: gönderilen hâlden boş blok atılıyor, sunucu onu geri döndürüyor, yerel taslak
   * hâlâ boş bloğu taşıdığı için "değişmiş" görünüyor ve döngü hiç durmuyordu.
   */
  const kirli =
    taslak !== null &&
    icerik.data !== undefined &&
    JSON.stringify(kaydedilebilir(taslak)) !== JSON.stringify(icerik.data.document);

  /**
   * OTOMATİK KAYDETME.
   *
   * ⚠ Yalnız KAYDEDİLMEMİŞ yerel değişiklik varken ve oturum hâlâ geçerliyken çalışıyor
   * (kartın runtime koşulu). `conflict` ve `blocked` durumlarında hiç tetiklenmiyor: ikisi
   * de kullanıcının kararını bekliyor.
   *
   * ⚠ Gecikme ve yeniden deneme aralığı POLİTİKADAN (`config/contractEditor.json`).
   * Sayıyı buraya yazsaydık kartın *"parametreler hard-code edilmez"* kuralı delinirdi.
   */
  useEffect(() => {
    if (!p || !icerik.data?.editable || taslak === null) return;

    const gecikme = otomatikKaydetmeGecikmesi({ durum, kirli, hataSayisi, politika: p });
    if (gecikme === null) return;

    zamanlayici.current = setTimeout(() => {
      setDurum("saving");
      kaydet.mutate({ document: kaydedilebilir(taslak), revision: icerik.data!.revision });
    }, gecikme);

    return () => {
      if (zamanlayici.current) clearTimeout(zamanlayici.current);
    };
    /**
     * ⚠ `taslak` BAĞIMLILIK LİSTESİNDE ve bu gerçek bir debounce sağlıyor: her tuş vuruşu
     * bekleyen zamanlayıcıyı iptal edip yenisini kuruyor.
     *
     * Listede olmasaydı zamanlayıcı, kurulduğu andaki taslağı KAPANIŞINDA taşırdı ve
     * sonradan yazılan her şey kaydedilmeden kaybolurdu — uçtan uca test tam olarak bunu
     * yakaladı: madde eklenip başlığı yazıldığında sunucuya BAŞLIKSIZ madde gidiyordu.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    taslak,
    kirli,
    durum,
    hataSayisi,
    p?.autosaveDelayMs,
    p?.retryDelayMs,
    icerik.data?.revision,
  ]);

  if (icerik.isPending) {
    return (
      <div role="status" aria-label="Sözleşme metni yükleniyor">
        <Skeleton lines={8} />
      </div>
    );
  }

  if (icerik.error || !icerik.data) {
    return (
      <div className="flex flex-col gap-4">
        <StateView
          state={resolveErrorState(icerik.error, {
            idempotent: true,
            overrides: {
              forbiddenOrMissing: {
                title: "Kayıt bulunamadı",
                description: "Bu sözleşme yok ya da erişiminiz bulunmuyor.",
              },
            },
          })}
          onRetry={() => void icerik.refetch()}
        />
      </div>
    );
  }

  const sunucu = icerik.data;
  const belge = taslak ?? sunucu.document;
  const duzenlenebilir = sunucu.editable;

  function guncelle(sonraki: Taslak) {
    setTaslak(sonraki);
    /** Yeni bir düzenleme, bekleyen bir arızayı da sıfırlıyor: yeniden denemeye değer. */
    if (durum !== "conflict" && durum !== "blocked") {
      setDurum("dirty");
      setHataSayisi(0);
    }
  }

  function maddeYaz(index: number, degisiklik: Partial<Taslak["clauses"][number]>) {
    guncelle({
      clauses: belge.clauses.map((c, i) => (i === index ? { ...c, ...degisiklik } : c)),
    });
  }

  function blokYaz(mi: number, bi: number, metin: string) {
    maddeYaz(mi, {
      blocks: belge.clauses[mi]!.blocks.map((b, i) => (i === bi ? { ...b, text: metin } : b)),
    });
  }

  function blokTuru(mi: number, bi: number, kind: ContractBlockKind) {
    maddeYaz(mi, {
      blocks: belge.clauses[mi]!.blocks.map((b, i) => (i === bi ? { ...b, kind } : b)),
    });
  }

  function tasi(index: number, yon: -1 | 1) {
    const hedef = index + yon;
    if (hedef < 0 || hedef >= belge.clauses.length) return;
    const yeni = [...belge.clauses];
    [yeni[index], yeni[hedef]] = [yeni[hedef]!, yeni[index]!];
    guncelle({ clauses: yeni });
  }

  return (
    <div className="flex flex-col gap-4">
      {/*
        ⚠ SAYFA BAŞLIĞI YOK — bu ekran artık KABUĞUN İÇİNDE, "Belge" bölümü olarak
        çiziliyor (`/app/sozlesmeler/:id/belge`). Kabuk sözleşmenin adını, numarasını ve
        durumunu zaten taşıyor; ikinci bir `<h1>` ("Sözleşme metni") aynı ekranda iki
        başlık yapardı ve hangisinin sayfa olduğu belirsizleşirdi.

        Kalan şerit bir BAŞLIK değil ARAÇ ÇUBUĞU: kaydetme durumu, Kaydet, Versiyon üret.
      */}
      <header className="flex flex-wrap items-center gap-3">
        <span className="mr-auto text-sm font-semibold text-fg-heading">Sözleşme metni</span>
        {sunucu.updatedByName ? (
          <span className="text-xs text-fg-muted">
            Son düzenleyen: {sunucu.updatedByName} · Sürüm {sunucu.revision}
          </span>
        ) : null}
        {duzenlenebilir ? (
          <>
            {/*
              ⚠ Kaydetme DURUMU her zaman görünür (kartın ilk kriteri). `role="status"`:
              durum değişimi ekran okuyucuya da bildiriliyor — "kaydedildi mi?" sorusu
              görme engelli kullanıcı için de cevaplanmalı.
            */}
            <span role="status" data-testid="kaydetme-durumu" className="text-xs text-fg-muted">
              {CONTRACT_SAVE_STATE_NAMES[durum]}
            </span>
            <Button
              type="button"
              loading={kaydet.isPending}
              onClick={() => {
                setDurum("saving");
                kaydet.mutate({ document: kaydedilebilir(belge), revision: sunucu.revision });
              }}
            >
              Kaydet
            </Button>
            {/*
              ⚠ Kaydedilmemiş değişiklik varken KAPALI: versiyon, metnin sunucudaki hâlini
              donduruyor. Açık bıraksaydık kullanıcı ekranında gördüğü cümleleri
              versiyonladığını sanır, tarihe başka bir metin geçerdi.
            */}
            <Button
              type="button"
              variant="secondary"
              loading={versiyonUret.isPending}
              disabled={durum !== "saved"}
              onClick={() => versiyonUret.mutate(sunucu.revision)}
            >
              Versiyon üret
            </Button>
          </>
        ) : null}
      </header>

      {/*
        ⚠ ÇAKIŞMA sessizce birleştirilmiyor; kullanıcıya KARŞILAŞTIRMA ve iki açık seçenek
        sunuluyor (kartın kuralı). Otomatik kaydetme bu ekran açıkken duruyor: devam etseydi
        her denemede aynı bayat sürümle çarpar ve kararı hiç sormazdı.
      */}
      {durum === "conflict" && sunucuHali ? (
        <Card>
          <div className="flex flex-col gap-3">
            <p className="text-sm font-medium text-fg-strong">
              Metin siz düzenlerken değişti
              {sunucuHali.updatedByName ? ` — son düzenleyen: ${sunucuHali.updatedByName}` : ""}.
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <p className="text-xs font-medium text-fg-muted">Sizin metniniz</p>
                <ul className="text-sm text-fg">
                  {belge.clauses.map((c) => (
                    <li key={`benim-${c.id}`}>{c.title || "(başlıksız madde)"}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs font-medium text-fg-muted">Sunucudaki metin</p>
                <ul className="text-sm text-fg">
                  {sunucuHali.document.clauses.map((c) => (
                    <li key={`sunucu-${c.id}`}>{c.title || "(başlıksız madde)"}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setTaslak(sunucuHali.document);
                  setDurum("saved");
                  setSunucuHali(null);
                }}
              >
                Sunucudaki metni yükle
              </Button>
              <Button
                type="button"
                onClick={() => {
                  /** KONTROLLÜ üzerine yazma: karar kullanıcının, sessiz değil. */
                  setDurum("saving");
                  kaydet.mutate({
                    document: kaydedilebilir(belge),
                    revision: sunucuHali.revision,
                  });
                }}
              >
                Benim metnimi uygula
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      {/*
        ⚠ Kart: *"bağlantı kesintisinde değişiklik kaybolmaz VEYA KAYBOLMA RİSKİ AÇIKLANIR."*
        Eşik politikadan: kaç denemeden sonra uyarının belirginleşeceği ürünün ayarı.
      */}
      {durum === "pending" && p && hataSayisi >= p.pendingWarningAfterFailures ? (
        <p role="alert" className="rounded-md border border-warning bg-canvas p-3 text-sm text-fg">
          Değişiklikleriniz sunucuya yazılamadı ve yalnız bu sekmede duruyor. Sayfayı kapatırsanız
          kaybolur.
        </p>
      ) : null}

      {/*
        ⚠ SALT OKUNUR sebebi AÇIKÇA yazılıyor. "Kaydet düğmesi yok" tek başına bir açıklama
        değil; kullanıcı düzenleyemediğini değil, ürünün bozuk olduğunu düşünürdü. Metin
        sunucunun sebep koduna karşılık geliyor — ekran kendi cümlesini uydurmuyor.
      */}
      {!duzenlenebilir && sunucu.lockReason ? (
        <div className="flex flex-col gap-2 rounded-md border border-border-default bg-canvas p-3">
          {/*
            ⚠ CÜMLE SUNUCUNUN SEBEBİNDEN VE KATALOGDAN türüyor, ekran kendi metnini
            yazmıyor. Eski hâli yalnız "bu durumdaki sözleşmenin metni değiştirilemez"
            diyordu ve üç soruyu cevapsız bırakıyordu: hangi durumdayım, hangi durumlarda
            düzenlenir, şimdi ne yapacağım.
          */}
          <p role="status" className="text-sm text-fg">
            {contractContentLockExplanation({
              reason: sunucu.lockReason,
              statusLabel: sunucu.statusLabel,
              editableStatusLabels: sunucu.editableStatusLabels,
              scanned: sunucu.scanned,
              ocrPageCount: sunucu.ocrPageCount,
              ocrConfidence: sunucu.ocrConfidence,
            })}
          </p>

          {/*
            ⚠ ÇIKIŞ YOLU (CLOS-634). Kilit "metin yüklenen dosyada" diyordu ve orada
            bitiyordu: kullanıcı yüklediği sözleşmeyi Closer içinde düzenleyemiyordu.
            Aksiyon yalnız sunucu AKTARILABİLİR dediğinde çıkıyor — taranmış bir PDF'te
            düğme göstermek, tıklayınca hata almak demek olurdu.
          */}
          {sunucu.adoptableFromUpload ? (
            <div className="flex flex-col gap-2">
              <p className="text-xs text-fg-soft">
                Yüklenen belgeden çıkarılan metni buraya aktarabilirsiniz. Kaynak dosya ve mevcut
                versiyon değişmez.
              </p>

              {/*
                ⚠ DÖNÜŞÜM KAYBI AKTARMA NOKTASINDA (CLOS-634 kriter 6): *"Dönüşüm kaybı ...
                kullanıcıya açık durum olarak gösterilir."*

                Uyarı yalnız ön izleme sayfasında duruyordu ve kullanıcı oraya uğramadan da
                aktarabiliyordu; tablonun düzleştiğini hiç öğrenmiyordu. Sessiz kayıp en
                tehlikeli hâli — ekranda gördüğü metni sözleşmenin tamamı sanar ve tablodaki
                bir bedeli kaçırır.

                ⚠ Kayıp YOKSA kutu hiç çizilmiyor: her aktarımda bir uyarı göstermek,
                gerçekten kayıp olan aktarımı gürültünün içinde kaybederdi.
              */}
              {sunucu.conversionLossNames.length > 0 ? (
                <div
                  role="status"
                  className="flex flex-col gap-1 rounded-md border border-border-default bg-warning-soft p-3"
                >
                  <p className="text-xs font-medium text-fg-strong">
                    Aktarılan metinde korunmayanlar
                  </p>
                  <ul className="list-disc pl-4 text-xs text-fg">
                    {sunucu.conversionLossNames.map((ad) => (
                      <li key={ad}>{ad}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={aktar.isPending}
                onClick={() => aktar.mutate()}
              >
                Yüklenen belgeden aktar
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {duzenlenebilir || belge.clauses.length > 0 ? (
        <>
          {/*
        ⚠ Yüzey seçimi kullanıcıda (CLOS-633). Yazarken belge, yapıyı düzenlerken maddeler.
        İki yüzey de AYNI taslağı görüyor: geçiş veri kaybettirmiyor.

        ⚠ Seçici de KOŞULUN İÇİNDE: kilitli ve metinsiz sözleşmede iki düğme çizilip
        altlarında hiçbir şey olmaması, kullanıcıya tıklanacak boş bir kabuk gösterirdi.
      */}
          <div role="group" aria-label="Yazım yüzeyi" className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant={yuzey === "belge" ? "primary" : "secondary"}
              onClick={() => setYuzey("belge")}
            >
              Belge
            </Button>
            <Button
              type="button"
              size="sm"
              variant={yuzey === "maddeler" ? "primary" : "secondary"}
              /** ⚠ Madde yüzeyinde yapılan düzenleme metne yansımalı: ham metin bırakılıyor. */
              onClick={() => {
                setYuzey("maddeler");
              }}
            >
              Maddeler
            </Button>
          </div>

          {/*
        ⚠ KİLİTLİ VE BOŞ BELGEDE YAZIM YÜZEYİ ÇİZİLMİYOR (gözle görüldü, 21 Ağustos).
        Ekran hem "uygulama içinde düzenlenebilir bir metni yok" diyor hem de altında boş
        bir yazı alanı gösteriyordu: kullanıcı yazmayı deneyip alanın kapalı olduğunu
        keşfediyordu. Kilitliyken gösterilecek bir metin varsa yüzey duruyor (salt okunur),
        yoksa yalnız sebep ve varsa çıkış yolu kalıyor.
      */}
          {yuzey === "belge" ? (
            <Card>
              <div className="flex flex-col gap-2">
                <h2 className="text-sm font-medium text-fg-strong">Sözleşme metni</h2>
                {/*
                  ⚠ YÜZEY ARTIK BELGE, METİN KUTUSU DEĞİL (CLOS-118'in 20 Ağustos kanonik
                  kararı · CLOS-634).

                  Burada bir `Textarea` vardı ve kartın kararı bunu açıkça yasaklıyordu:
                  *"birincil sözleşme düzenleme deneyimi belge/sayfa odaklıdır ... beyaz
                  sayfa ... başlık, paragraf, kalın/italik, liste, hizalama"*. Metin
                  kutusunda bunların hiçbiri yok. Kart yine de Done kapanmıştı; kusuru
                  müşteri bildirdi (4 Eylül).

                  ⚠ Yazma TALİMATI da kalktı: "boş satırla ayırın, satır başına - koyun"
                  düz metin yüzeyinin kuralıydı. Belge yüzeyinde kullanıcı doğrudan yazıyor
                  ve biçimi çubuktan veriyor; eski talimatı bırakmak ekranı yapmadığı bir
                  şeyi anlatır hâlde bırakırdı.
                */}
                <ContractDocumentEditor
                  contractId={id!}
                  belge={belge}
                  duzenlenebilir={duzenlenebilir}
                  onChange={guncelle}
                />
              </div>
            </Card>
          ) : null}

          {yuzey === "maddeler" && belge.clauses.length === 0 ? (
            <Card>
              <p className="text-sm text-fg-muted">
                Bu sözleşmenin henüz metni yok.
                {duzenlenebilir ? " İlk maddeyi ekleyerek başlayabilirsiniz." : ""}
              </p>
            </Card>
          ) : null}

          {(yuzey === "maddeler" ? belge.clauses : []).map((madde, mi) => (
            <Card key={madde.id}>
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <label className="sr-only" htmlFor={`madde-baslik-${madde.id}`}>
                    {mi + 1}. madde başlığı
                  </label>
                  <Input
                    id={`madde-baslik-${madde.id}`}
                    className="flex-1"
                    placeholder="Madde başlığı"
                    value={madde.title}
                    disabled={!duzenlenebilir}
                    onChange={(e) => maddeYaz(mi, { title: e.target.value })}
                  />
                  {duzenlenebilir ? (
                    <div
                      role="group"
                      aria-label={`${mi + 1}. madde işlemleri`}
                      className="flex gap-1"
                    >
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => tasi(mi, -1)}
                        disabled={mi === 0}
                      >
                        Yukarı
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => tasi(mi, 1)}
                        disabled={mi === belge.clauses.length - 1}
                      >
                        Aşağı
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          guncelle({ clauses: belge.clauses.filter((_, i) => i !== mi) })
                        }
                      >
                        Maddeyi sil
                      </Button>
                    </div>
                  ) : null}
                </div>

                {madde.blocks.map((blok, bi) => (
                  <div key={`${madde.id}-${bi}`} className="flex flex-col gap-1">
                    <label className="sr-only" htmlFor={`blok-${madde.id}-${bi}`}>
                      {mi + 1}. madde {bi + 1}. blok
                    </label>
                    <Textarea
                      id={`blok-${madde.id}-${bi}`}
                      rows={4}
                      value={blok.text}
                      disabled={!duzenlenebilir}
                      onChange={(e) => blokYaz(mi, bi, e.target.value)}
                    />
                    {duzenlenebilir ? (
                      <div className="flex gap-2">
                        {/*
                          ⚠ İKİLİ GEÇİŞ YERİNE TAM SEÇİM (ADR-0062). Burada "Madde işaretine
                          çevir / Paragrafa çevir" diye bir geçiş vardı ve blok modeli
                          yalnız o ikisini taşırken doğruydu. Model başlık ve numaralı madde
                          de taşıyınca geçiş SESSİZ KAYIP üretmeye başladı: bir başlık
                          bloğunun yanında "Madde işaretine çevir" yazıyordu ve basılınca
                          başlık kayboluyordu — kullanıcı bunu ancak belge yüzeyine dönünce
                          görürdü.
                        */}
                        <label className="sr-only" htmlFor={`blok-turu-${madde.id}-${bi}`}>
                          {bi + 1}. blok türü
                        </label>
                        <Select
                          id={`blok-turu-${madde.id}-${bi}`}
                          value={blok.kind}
                          onChange={(e) => blokTuru(mi, bi, e.target.value as ContractBlockKind)}
                        >
                          {CONTRACT_BLOCK_KINDS.map((tur) => (
                            <option key={tur} value={tur}>
                              {CONTRACT_BLOCK_KIND_NAMES[tur]}
                            </option>
                          ))}
                        </Select>
                      </div>
                    ) : null}
                  </div>
                ))}

                {duzenlenebilir ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      maddeYaz(mi, { blocks: [...madde.blocks, { kind: "paragraph", text: "" }] })
                    }
                  >
                    Blok ekle
                  </Button>
                ) : null}
              </div>
            </Card>
          ))}

          {/* ⚠ "Madde ekle" yalnız MADDE yüzeyinde: belge yüzeyinde madde açmak boş satır yazmak. */}
          {duzenlenebilir && yuzey === "maddeler" ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => guncelle({ clauses: [...belge.clauses, bosMadde()] })}
            >
              Madde ekle
            </Button>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
